import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Clapperboard, Send, Sparkles, Film, Coins, Loader2, CheckCircle,
  Play, AlertTriangle, Users, MessageSquare, RotateCcw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { AssistantChat } from "../components/AssistantChat";
import { iosSave } from "../lib/iosSave";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { ShareToCommunityButton } from "../components/community/ShareToCommunityButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MovieStudioPage = () => {
  const { token, user } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [seedIdea, setSeedIdea] = useState("");
  const [session, setSession] = useState(null);    // {id, messages, draft_script, status, movie_id}
  const [creating, setCreating] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [sending, setSending] = useState(false);
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [starting, setStarting] = useState(false);
  const [movie, setMovie] = useState(null);        // movie_tasks doc when generating
  const [limits, setLimits] = useState(null);      // { max_shots, max_seconds, tier }
  const chatEndRef = useRef(null);

  // Restore an in-flight session after navigation — the session (and any
  // generating movie) lives server-side, so leaving the page never loses it.
  useEffect(() => {
    if (!token) return;
    const sid = localStorage.getItem("xea-movie-session-id");
    if (!sid) return;
    axios.get(`${API}/movie/sessions/${sid}`, { headers })
      .then((r) => setSession(r.data))
      .catch(() => localStorage.removeItem("xea-movie-session-id"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Fetch tier-aware caps once
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/movie/limits`, { headers })
      .then((r) => setLimits(r.data))
      .catch(() => {});
  }, [token, headers]);

  // Scroll the chat to bottom on every new message
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [session?.messages]);

  // ------------------------------------------------------------------
  // Movie status polling — once a session has a movie_id, poll every 4s
  // until the movie is done or failed.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!session?.movie_id) return;
    let stopped = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/movie/${session.movie_id}/status`, { headers });
        if (stopped) return;
        setMovie(r.data);
        if (["done", "failed"].includes(r.data.status)) return;
        setTimeout(tick, 4000);
      } catch (e) {
        if (!stopped) setTimeout(tick, 6000);
      }
    };
    tick();
    return () => { stopped = true; };
  }, [session?.movie_id, headers]);

  // ------------------------------------------------------------------
  // Session lifecycle
  // ------------------------------------------------------------------
  const createSession = async () => {
    if (!seedIdea.trim()) {
      toast.error("Tell me what kind of short film you want to make.");
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/movie/sessions`,
        { initial_idea: seedIdea.trim() },
        { headers });
      setSession(r.data);
      localStorage.setItem("xea-movie-session-id", r.data.id);
      setSeedIdea("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start session.");
    } finally {
      setCreating(false);
    }
  };

  const sendChat = async () => {
    if (!chatInput.trim() || !session) return;
    const text = chatInput.trim();
    setChatInput("");
    setSending(true);
    // Optimistic append
    setSession((s) => ({
      ...s,
      messages: [...(s.messages || []), { role: "user", content: text, ts: new Date().toISOString() }],
    }));
    try {
      const r = await axios.post(`${API}/movie/sessions/${session.id}/chat`,
        { message: text }, { headers });
      setSession((s) => ({
        ...s,
        messages: [...(s.messages || []), { role: "assistant", content: r.data.assistant, ts: new Date().toISOString() }],
        draft_script: r.data.draft_script ?? s.draft_script,
        status: r.data.status,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Planner is busy — retry.");
    } finally {
      setSending(false);
    }
  };

  const openEstimate = async () => {
    if (!session?.draft_script) {
      toast.error("Keep chatting until the planner builds a storyboard.");
      return;
    }
    try {
      const r = await axios.get(`${API}/movie/sessions/${session.id}/estimate`, { headers });
      setEstimate(r.data);
      setEstimateOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not estimate cost.");
    }
  };

  const startMovie = async () => {
    if (!session?.draft_script) return;
    setStarting(true);
    try {
      const r = await axios.post(`${API}/movie/sessions/${session.id}/start`,
        { script: session.draft_script }, { headers });
      toast.success(`Movie queued! Charged ${r.data.tokens_charged} tokens.`);
      setEstimateOpen(false);
      setSession((s) => ({ ...s, status: "generating", movie_id: r.data.movie_id }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start movie.");
    } finally {
      setStarting(false);
    }
  };

  const resetSession = () => {
    localStorage.removeItem("xea-movie-session-id");
    setSession(null);
    setMovie(null);
    setEstimate(null);
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (!session) {
    return <SeedPanel
      seedIdea={seedIdea} setSeedIdea={setSeedIdea}
      creating={creating} createSession={createSession}
      userTokens={user?.tokens} limits={limits} />;
  }

  return (
    <div className="space-y-6" data-testid="movie-studio-page">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-fuchsia-600">
            Movie Mode · BETA
          </p>
          <h1 className="text-2xl sm:text-3xl font-black text-ink-950 tracking-tight">
            From nothing to a short film.
          </h1>
          <p className="text-sm text-ink-700 mt-1">
            Chat with XEA, lock the storyboard, then watch it render shot by shot.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
            <Coins className="w-3 h-3 mr-1" />
            {user?.tokens?.toLocaleString() || 0} tokens
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={resetSession}
            data-testid="movie-new-session-btn"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" />
            New
          </Button>
        </div>
      </header>

      {/* Unmissable render CTA — appears the moment the storyboard is locked */}
      {session.status === "ready" && session.draft_script && !session.movie_id && !movie && (
        <div
          className="rounded-3xl border-2 border-fuchsia-300 bg-gradient-to-r from-fuchsia-50 to-violet-50 p-5 flex flex-wrap items-center justify-between gap-3"
          data-testid="movie-ready-banner"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-fuchsia-500 flex items-center justify-center shrink-0 animate-pulse">
              <Clapperboard className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-black text-ink-950">Your storyboard is ready!</p>
              <p className="text-xs text-ink-600">
                Nothing renders (or gets charged) until you approve — review the cost and hit render.
              </p>
            </div>
          </div>
          <Button
            onClick={openEstimate}
            className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-bold"
            data-testid="movie-ready-banner-btn"
          >
            <Sparkles className="w-4 h-4 mr-1.5" />
            Approve & Render
          </Button>
        </div>
      )}

      {movie && movie.status !== "done" && movie.status !== "failed" && (
        <RenderProgress movie={movie} />
      )}

      {movie && movie.status === "done" && (
        <FinalMovie movie={movie} />
      )}

      {movie && movie.status === "failed" && (
        <div
          className="rounded-3xl border border-rose-200 bg-rose-50 p-5 text-rose-700"
          data-testid="movie-failed-banner"
        >
          <div className="flex items-center gap-2 font-bold mb-1">
            <AlertTriangle className="w-4 h-4" />
            Movie failed
          </div>
          <p className="text-sm">{movie.error || "Unknown error"}. Your tokens were refunded.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Chat planner */}
        <section
          className="lg:col-span-3 bg-white border border-ink-200 rounded-3xl p-5 flex flex-col"
          data-testid="movie-chat-panel"
        >
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-fuchsia-600" />
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-fuchsia-600">
              Planner Chat
            </p>
          </div>

          <div className="flex-1 min-h-[260px] max-h-[520px] overflow-y-auto space-y-3 pr-2 mb-3" data-testid="movie-chat-messages">
            {(session.messages || []).map((m, i) => (
              <ChatBubble key={i} role={m.role} text={m.content} />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-ink-500 text-sm">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                XEA is thinking...
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="flex gap-2">
            <Textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Reply, refine, or say: build the storyboard"
              rows={2}
              className="bg-ink-50 border-ink-200 text-ink-950 text-sm resize-none"
              data-testid="movie-chat-input"
              disabled={sending || session.status === "generating"}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendChat();
                }
              }}
            />
            <Button
              onClick={sendChat}
              disabled={sending || !chatInput.trim() || session.status === "generating"}
              className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white"
              data-testid="movie-chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </section>

        {/* Storyboard panel */}
        <section className="lg:col-span-2 space-y-4">
          <StoryboardPanel
            script={session.draft_script}
            onOpenEstimate={openEstimate}
            disabled={session.status === "generating" || !!session.movie_id}
          />
        </section>
      </div>

      {/* Token estimate confirm modal */}
      <Dialog open={estimateOpen} onOpenChange={setEstimateOpen}>
        <DialogContent className="bg-white border-ink-200" data-testid="movie-estimate-dialog">
          <DialogHeader>
            <DialogTitle className="text-ink-950 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-fuchsia-500" />
              Ready to render?
            </DialogTitle>
          </DialogHeader>
          {estimate && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2 text-center">
                <Stat label="Shots" value={estimate.shot_count} />
                <Stat label="Duration" value={`${estimate.total_seconds}s`} />
                <Stat label="Lines" value={estimate.dialogue_lines} />
                <Stat label="Scenes" value={estimate.scene_count} />
              </div>

              {/* Receipt — transparent cost breakdown */}
              {estimate.breakdown && (
                <div className="rounded-2xl border border-ink-200 bg-ink-50 p-3 text-xs space-y-1" data-testid="movie-estimate-breakdown">
                  <ReceiptLine label={`${estimate.shot_count} Kling shots`} value={estimate.breakdown.shots} />
                  <ReceiptLine label={`${estimate.portrait_count || 0} Nano Banana portraits`} value={estimate.breakdown.portraits} />
                  <ReceiptLine label={`${estimate.dialogue_lines} ElevenLabs voice lines`} value={estimate.breakdown.dialogue} />
                  <ReceiptLine label={`${estimate.scene_count} Mubert music beds`} value={estimate.breakdown.music} />
                </div>
              )}

              <div
                className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-center justify-between"
                data-testid="movie-estimate-cost"
              >
                <span className="text-ink-950 font-bold flex items-center gap-2">
                  <Coins className="w-4 h-4 text-amber-600" /> Total cost
                </span>
                <span className="text-2xl font-black text-amber-700">
                  {estimate.tokens}
                </span>
              </div>
              <p className="text-xs text-ink-500">
                Rendering typically takes 6-15 minutes (3 shots in parallel via Kling).
                {` You'll get an email when it's ready. If anything fails, tokens are auto-refunded.`}
              </p>
              <Button
                onClick={startMovie}
                disabled={starting || (user?.tokens || 0) < estimate.tokens}
                className="w-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white"
                data-testid="movie-start-confirm-btn"
              >
                {starting ? "Starting..." : (user?.tokens || 0) < estimate.tokens
                  ? "Not enough tokens"
                  : `Approve & Render (${estimate.tokens} tokens)`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =====================================================================
// Sub-components
// =====================================================================

const SeedPanel = ({ seedIdea, setSeedIdea, creating, createSession, userTokens, limits }) => {
  const isUltimate = limits?.tier === "platinum" || limits?.tier === "ultimate";
  const maxShots   = limits?.max_shots || 20;
  const maxMins    = Math.round((limits?.max_seconds || 240) / 60);
  return (
  <div className="space-y-6" data-testid="movie-seed-panel">
    <header>
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-fuchsia-600">
        Movie Mode · BETA
      </p>
      <h1 className="text-3xl sm:text-4xl font-black text-ink-950 tracking-tight">
        Write a tiny film. From nothing.
      </h1>
      <p className="text-ink-700 text-sm mt-2 max-w-2xl">
        Tell XEA the kind of short film you want. The planner will ask the right
        questions, build a structured storyboard, and render it shot by shot —
        Kling visuals, ElevenLabs voices, music, the whole thing.
      </p>
    </header>

    {/* AI assistant — brainstorm the film idea before seeding the planner */}
    <div className="rounded-2xl border border-fuchsia-200 bg-gradient-to-br from-fuchsia-50/40 to-white p-3">
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-fuchsia-600 mb-2 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" /> Not sure yet? Brainstorm with XEA
      </p>
      <AssistantChat
        variant="embed"
        contextHint="Pitch me a mood, a genre, or half an idea — I'll shape it into a short-film concept the planner can run with."
        usePromptLabel="Use as movie idea"
        onUsePrompt={(text) => {
          setSeedIdea(text.trim().slice(0, 2000));
          toast.success("Idea dropped into the film brief below");
        }}
      />
    </div>

    <div className="bg-white border border-ink-200 rounded-3xl p-6 space-y-4">
      <Textarea
        value={seedIdea}
        onChange={(e) => setSeedIdea(e.target.value)}
        rows={5}
        placeholder="Example: A 90-second neon-noir romance between a courier drone and a vintage radio operator, bittersweet ending..."
        className="bg-ink-50 border-ink-200 text-ink-950 resize-none"
        data-testid="movie-seed-input"
        maxLength={2000}
      />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-ink-500 flex items-center gap-3 flex-wrap" data-testid="movie-tier-caps">
          <span>
            Your tier · up to <span className="font-bold text-ink-950">{maxShots} shots</span> /
            {" "}<span className="font-bold text-ink-950">{maxMins} min</span>
            {isUltimate ? "  · Platinum unlocked" : "  · upgrade for 10-min movies"}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
            <Coins className="w-3 h-3 mr-1" />
            {userTokens?.toLocaleString() || 0} tokens
          </Badge>
          <Button
            onClick={createSession}
            disabled={creating || !seedIdea.trim()}
            className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white"
            data-testid="movie-seed-start-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Clapperboard className="w-4 h-4 mr-1" />}
            Start planning
          </Button>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <HintCard icon={MessageSquare} title="Chat your story" body="XEA's planner asks the right questions. You stay creative, it stays structured." />
      <HintCard icon={Users} title="Cast & voices" body="Each character gets a Nano Banana portrait and a cinematic ElevenLabs voice." />
      <HintCard icon={Film} title="Render in parallel" body="3 shots at a time via Kling. Music ducked under dialogue. Auto-refund if anything fails." />
    </div>
  </div>
  );
};

const ChatBubble = ({ role, text }) => {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`chat-bubble-${role}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
          isUser
            ? "bg-fuchsia-500 text-white"
            : "bg-ink-100 text-ink-950 border border-ink-200"
        }`}
      >
        {text}
      </div>
    </div>
  );
};

const StoryboardPanel = ({ script, onOpenEstimate, disabled }) => {
  if (!script) {
    return (
      <div
        className="bg-white border border-ink-200 rounded-3xl p-6 text-center text-ink-500"
        data-testid="movie-storyboard-empty"
      >
        <Clapperboard className="w-8 h-8 text-ink-300 mx-auto mb-2" />
        <p className="text-sm">Your storyboard appears here as the planner locks down the script.</p>
      </div>
    );
  }

  const totalShots = (script.scenes || []).reduce((acc, s) => acc + (s.shots || []).length, 0);
  const totalSecs  = (script.scenes || []).reduce(
    (acc, s) => acc + (s.shots || []).reduce((a, sh) => a + (sh.duration || 8), 0), 0
  );

  return (
    <div className="bg-white border border-ink-200 rounded-3xl p-5 space-y-4" data-testid="movie-storyboard">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-fuchsia-600">
            Storyboard
          </p>
          <h2 className="text-lg font-black text-ink-950 leading-tight" data-testid="movie-title">
            {script.title || "Untitled"}
          </h2>
          <p className="text-xs text-ink-500 mt-1">
            {(script.genre || "—")} · {(script.tone || "—")} · {totalShots} shots · {totalSecs}s
          </p>
        </div>
      </div>

      {script.logline && (
        <p className="text-sm italic text-ink-700 border-l-2 border-fuchsia-300 pl-3">
          {`"${script.logline}"`}
        </p>
      )}

      {/* Characters */}
      {(script.characters || []).length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-500 mb-1">Cast</p>
          <div className="flex flex-wrap gap-2">
            {script.characters.map((c, i) => (
              <Badge key={i} variant="outline" className="text-xs border-ink-300" data-testid={`cast-${c.id}`}>
                {c.name} · {c.archetype?.replace(/_/g, " ")}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Scenes / Shots */}
      <div className="max-h-[420px] overflow-y-auto space-y-3 pr-1" data-testid="movie-scenes-list">
        {(script.scenes || []).map((scene, si) => (
          <div key={si} className="border border-ink-100 rounded-2xl p-3 bg-ink-50">
            <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-500 mb-1">
              Scene {si + 1}{scene.music_vibe ? ` · ${scene.music_vibe}` : ""}
            </p>
            <p className="text-sm text-ink-950 font-medium mb-2">{scene.summary}</p>
            <ol className="space-y-1.5">
              {(scene.shots || []).map((sh, shi) => (
                <li key={shi} className="text-xs text-ink-700" data-testid={`shot-${scene.id}-${sh.id}`}>
                  <span className="font-bold text-fuchsia-600">{shi + 1}. </span>
                  <span className="text-ink-500">[{sh.duration || 8}s]</span>{" "}
                  {sh.prompt}
                  {(sh.dialogue || []).length > 0 && (
                    <ul className="ml-4 mt-1 space-y-0.5">
                      {sh.dialogue.map((d, di) => (
                        <li key={di} className="text-ink-600 italic">
                          🎤 {d.line}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>

      <Button
        onClick={onOpenEstimate}
        disabled={disabled}
        className="w-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white"
        data-testid="movie-approve-btn"
      >
        <Sparkles className="w-4 h-4 mr-1" />
        Approve & see cost
      </Button>
    </div>
  );
};

const RenderProgress = ({ movie }) => (
  <div
    className="rounded-3xl border border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 to-violet-50 p-5"
    data-testid="movie-progress-banner"
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Loader2 className="w-4 h-4 text-fuchsia-600 animate-spin" />
        <p className="font-bold text-ink-950">Rendering your movie</p>
      </div>
      <span className="text-sm font-bold text-fuchsia-700" data-testid="movie-progress-pct">
        {movie.progress || 0}%
      </span>
    </div>
    <div className="h-2 bg-fuchsia-100 rounded-full overflow-hidden mb-2">
      <div
        className="h-full bg-fuchsia-500 transition-all"
        style={{ width: `${movie.progress || 0}%` }}
      />
    </div>
    <p className="text-xs text-ink-700">
      <span className="font-mono">{movie.current_step?.replace(/_/g, " ")}</span>
      {" · "}
      {`It's safe to leave this page — we'll email you when it's done.`}
    </p>
  </div>
);

const FinalMovie = ({ movie }) => {
  const logline = movie?.script?.logline || movie?.script?.title || "";
  return (
  <div
    className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5"
    data-testid="movie-done-banner"
  >
    <div className="flex items-center gap-2 mb-3">
      <CheckCircle className="w-5 h-5 text-emerald-600" />
      <p className="font-bold text-ink-950">Your short film is ready!</p>
    </div>
    <video
      src={movie.final_url}
      controls
      className="w-full max-h-[480px] rounded-2xl bg-black"
      data-testid="movie-player"
    />
    <div className="flex flex-wrap gap-2 mt-3">
      <Button
        size="sm"
        className="bg-emerald-500 hover:bg-emerald-600 text-white"
        onClick={() => iosSave(movie.final_url, { kind: "video", filename: `xea-movie-${movie.id || "film"}.mp4` })}
        data-testid="movie-download-btn"
      >
        <Play className="w-3.5 h-3.5 mr-1" /> Download
      </Button>
      <ShareToCommunityButton
        sourceCollection="movie_tasks"
        sourceId={movie.id}
        defaultCaption={logline}
        size="sm"
        variant="default"
        className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white border-fuchsia-500"
        testId="movie-share-btn"
      />
    </div>
  </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-ink-50 border border-ink-200 rounded-xl p-3">
    <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-500">{label}</p>
    <p className="text-lg font-black text-ink-950 mt-0.5">{value}</p>
  </div>
);

const ReceiptLine = ({ label, value }) => (
  <div className="flex items-center justify-between" data-testid="receipt-line">
    <span className="text-ink-700">{label}</span>
    <span className="font-mono font-bold text-ink-950">{value} tk</span>
  </div>
);

const HintCard = ({ icon: Icon, title, body }) => (
  <div className="bg-white border border-ink-200 rounded-2xl p-4">
    <Icon className="w-5 h-5 text-fuchsia-500 mb-2" />
    <p className="text-sm font-bold text-ink-950">{title}</p>
    <p className="text-xs text-ink-600 mt-1 leading-snug">{body}</p>
  </div>
);

export default MovieStudioPage;
